import { Injectable } from '@angular/core'
import { Product, ProductsService } from '../products'
import {
  Observable,
  catchError,
  concatMap,
  forkJoin,
  from,
  map,
  of,
  reduce,
  switchMap,
  tap,
} from 'rxjs'
import { getOrderSimpleProductIds } from './libs/order.lib'
import {
  Order,
  OrderDetail,
  OrderPackagesData,
  OrderShipmentData,
} from './models/order.entity'
import { OrdersService } from './orders.service'
import {
  Carrier,
  CarriersService,
  generateCarrierTrackingUrl,
  isGenericCarrier,
} from '../carriers'
import {
  PickingList,
  PickingListOrderRow,
  PickingListsService,
  PickingUnusableProduct,
} from '../picking-lists'
import { getOrderPackagesProductIds } from './libs/order-packages.lib'
import { PackingList, PackingListsService } from '../packing-lists'
import { Bordereau, BordereausService } from '../bordereaus'
import {
  ModalResponse,
  NgModalServiceInterface,
} from '../../models/modal.model'
import {
  OrderModalCode,
  OrderNotificationCode,
  parseOrderModal,
  parseOrderNotification,
} from './libs/order-notification.lib'
import { Coverage } from './models/order.types'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import { HttpResponse } from '@angular/common/http'
import { OrdersDetailLoader } from './loaders/orders-detail.loader'
import { OrdersListingLoader } from './loaders/orders-listing.loader'
import { OrderField, OrderSearchParams } from './models/order-search.model'
import { ORDER_ACTIONS } from './consts/order-statuses.const'
import {
  OrdersListingData,
  OrdersListingPage,
} from './models/order-listing.model'
import { chunk } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class OrdersRepository {
  constructor(
    private ordersService: OrdersService,
    private packingListsService: PackingListsService,
    private pickingListsService: PickingListsService,
    private carriersService: CarriersService,
    private bordereausService: BordereausService,
    private productsService: ProductsService,
    private ordersDetailLoader: OrdersDetailLoader,
    private ordersListingLoader: OrdersListingLoader,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Load order products
   * @param orderId - the order ID
   * @returns the observable for load order products
   */
  getOrderProducts$(
    orderId: string,
    products?: Product[],
  ): Observable<Product[]> {
    return this.ordersService.read$(orderId).pipe(
      map((order) => getOrderSimpleProductIds(order)),
      switchMap((productIds) =>
        this.productsService.store$(productIds, products || [], true),
      ),
    )
  }

  /**
   * Load order packages
   * @param order - the order
   * @param products - the products already loaded
   * @returns the observable for load the order packages data
   */
  getOrderPackagesData$(
    order: Order,
    products?: Product[],
  ): Observable<OrderPackagesData> {
    const productIds = getOrderPackagesProductIds(
      order.packages,
      order.additionalPackages,
    )
    return this.productsService.store$(productIds, products || []).pipe(
      map((products) => ({
        products,
        packages: order.packages,
        additionalPackages: order.additionalPackages,
      })),
    )
  }

  /**
   * Search product package by code
   * @param code - the search code
   * @returns the observable for get product package
   */
  getProductPackage$(
    code: string | undefined,
  ): Observable<Product | undefined> {
    // Check code length
    if (!code || !code.length) {
      this.notify({ code: 'SKU_NOT_VALID' })
      return of()
    }

    return this.productsService.readOne$({ code }).pipe(
      catchError(() => {
        this.notify({ code: 'PACKAGE_NOT_FOUND' })
        return of()
      }),
      switchMap((product) => {
        if (product) {
          return of(product)
        } else {
          this.notify({ code: 'PACKAGE_NOT_AVAILABLE' })
          return of()
        }
      }),
    )
  }

  /**
   * Load order shipment data
   * @param order - the order shipment data
   * @param carrier - the carrier of the order
   * @returns the observable for load the order shipment data
   */
  getOrderShipmentData$(
    order: Order,
    carrier?: Carrier,
  ): Observable<OrderShipmentData | undefined> {
    // Check order status
    // TODO: this not should be here
    const orderPermissions = ORDER_ACTIONS[order.status].permissions
    if (!orderPermissions.includes('showShipmentInfo')) {
      return of(undefined)
    }

    // Check order transport
    if (order.header.transport !== 'CARRIER' || !order.carrierId) {
      return of(undefined)
    }

    // Retrieve carrier if not passed
    const obs$ = carrier
      ? of(carrier)
      : this.carriersService.read$(order.carrierId)

    return obs$.pipe(
      switchMap((carrier) => this.getOrderCarrierShipment$(order._id, carrier)),
    )
  }

  /**
   * Get order shipment data by carrier
   * @param orderId - the order ID
   * @param carrier - the carrier of the order
   * @returns the observable for
   */
  getOrderCarrierShipment$(
    orderId: string,
    carrier: Carrier,
  ): Observable<OrderShipmentData | undefined> {
    if (isGenericCarrier(carrier)) {
      return of(undefined)
    }

    return this.ordersService.getShipment$(orderId).pipe(
      map((shipment) => {
        if (!shipment.trackingNumbers?.length) {
          return undefined
        }

        return {
          shipment,
          tracking: {
            number: shipment.trackingNumbers[0],
            link: generateCarrierTrackingUrl(carrier, shipment),
          },
        }
      }),
    )
  }

  /**
   * Load picking-list order IDs
   * @param pickingListId - the order ID
   * @returns the observable for load picking-list order IDs
   */
  getPickingListIds$(pickingListId: string): Observable<string[]> {
    return this.pickingListsService
      .read$(pickingListId)
      .pipe(map((pickingList) => pickingList.orders.map((o) => o._id)))
  }

  /**
   * Download order label
   * @param orderId - the order ID
   * @returns the observable for download the order label
   */
  downloadOrderLabel$(
    order: Order,
  ): Observable<(HttpResponse<Blob> | undefined)[]> {
    const shipment = order.shipments?.find((shipment) => {
      return shipment.status === 'SHIPPED' || shipment.status === 'REQUESTED'
    })
    if (!shipment?.labels?.length) {
      return of([])
    }
    const downloads$ = shipment.labels.map((label) =>
      label._id
        ? this.ordersService.downloadLabel$(order._id, label._id)
        : of(undefined),
    )
    return forkJoin(downloads$)
  }

  /**
   * Converts a ZPL order label into a PDF file
   * @param zpl - The label in ZPL format
   * @returns The observable<any> for download the order label
   */
  downloadPdfLabel(zpl: string): Observable<Blob> {
    return this.ordersService.downloadPdfLabel$(zpl)
  }

  /**
   * Load order picking-list
   * @param order - the order
   * @returns the observable for load order picking-list
   */
  loadOrderPickingList$(
    pickingListId?: string,
  ): Observable<PickingList | undefined> {
    if (!pickingListId) {
      return of(undefined)
    }

    return this.pickingListsService.read$(pickingListId)
  }

  /**
   * Load order packing-list
   * @param order - the order
   * @returns the observable for load order packing-list
   */
  loadOrderPackingList$(order: Order): Observable<PackingList | undefined> {
    if (!order.pickingListId) {
      return of(undefined)
    }

    return this.packingListsService
      .list$({
        pickingListId: order.pickingListId,
      })
      .pipe(map((packings) => packings[0]))
  }

  /** Keeps each products `list$` under CrudService `defaultLimit` (50) so `returnAll` does not open extra paginated searches. */
  private static readonly loadOrderProductsIdChunk = 50

  loadOrderProducts$(order: Order): Observable<Product[]> {
    if (!order.rows.length) {
      return of([])
    }

    const productIds = getOrderSimpleProductIds(order)

    if (productIds.length < 1) {
      return of([])
    }

    const fields = [
      '_id',
      'warehouses',
      'productType',
      'onHandQty',
      'availableQty',
      'incomingQty',
      'receivingQty',
      'grossWeight',
      'netWeight',
      'volume.total',
    ] as const

    const idChunks = chunk(
      productIds,
      OrdersRepository.loadOrderProductsIdChunk,
    )

    return from(idChunks).pipe(
      concatMap((ids) =>
        this.productsService.list$(
          {
            fields: [...fields],
            _id: ids,
          },
          true,
        ),
      ),
      reduce((acc, products) => acc.concat(products), [] as Product[]),
    )
  }

  /**
   * Load order bordereau
   * @param order - the order
   * @returns the observable for load order bordereau
   */
  loadOrderBordereau$(order: Order): Observable<Bordereau | undefined> {
    if (!order.bordereauId) {
      return of(undefined)
    }

    return this.bordereausService.read$(order.bordereauId)
  }

  /**
   * Load order simple products
   * @param order - the order
   * @returns the observable for load order simple products
   */
  loadOrderSimpleProducts$(order: Order): Observable<Product[]> {
    const productIds = getOrderSimpleProductIds(order)

    if (!productIds.length) {
      return of([])
    }

    return this.productsService.list$({ _id: productIds })
  }

  /**
   * Load order coverage
   * @param warehouseId - the warehouseId
   * @param orderId - the orderId
   * @returns the observable for load order coverage
   */
  loadOrderCoverage$(
    warehouseId: string,
    orderId: string,
  ): Observable<{
    coverage: Coverage
    rows: PickingListOrderRow[]
    unusable: PickingUnusableProduct[]
  }> {
    return this.pickingListsService
      .simulate$(warehouseId, [{ _id: orderId }])
      .pipe(
        map((simulation) => {
          const orderCoverage = simulation.orders.find((o) => o._id === orderId)

          return {
            coverage: orderCoverage?.status || 'UNAVAILABLE',
            rows: orderCoverage?.rows || [],
            unusable:
              simulation.unusable?.filter((r) => r.orderId === orderId) || [],
          }
        }),
      )
  }

  /**
   * Check product barcode
   * @param order - the order
   * @param barcode - the product barcode
   * @returns the observable for check if a product is inside the an order
   */
  checkOrderProductBarcode$(order: Order, code: string): Observable<number> {
    return this.productsService.readOne$({ code }).pipe(
      map((product) =>
        order.rows.findIndex(
          (orderRow) => orderRow.product._id === product._id,
        ),
      ),
      tap((rowIndex) => {
        if (rowIndex < 0) {
          throw new Error('PRODUCT_NOT_FOUND')
        }
      }),
    )
  }

  loadOrder$(orderId: string, fields: OrderField[]): Observable<OrderDetail> {
    return this.ordersDetailLoader.loadOrder$(orderId, fields)
  }

  saveOrder$(order: Order, fields: OrderField[]): Observable<OrderDetail> {
    return this.ordersDetailLoader.saveOrder$(order, fields)
  }

  searchOrders$(
    searchParams: OrderSearchParams,
    fields: OrderField[],
    listingData?: OrdersListingData,
    returnAll?: boolean,
  ): Observable<OrdersListingPage> {
    return this.ordersListingLoader.searchOrders$(
      searchParams,
      fields,
      listingData,
      returnAll,
    )
  }

  notify(options: { code?: OrderNotificationCode; message?: string }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseOrderNotification(options.code),
    )
  }

  async alert(
    code: OrderModalCode,
    order?: Order,
    status?: string,
  ): Promise<ModalResponse> {
    const settings = parseOrderModal(code, order, status)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
